import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import ChatWidget from './ChatWidget';
import ThemeToggle, { ThemeSegmentedControl } from './ThemeToggle';
import EmailVerificationBanner from './EmailVerificationBanner';
import WorkflowNotificationBell from './workflows/WorkflowNotificationBell';
import PlatformSidebar from './navigation/PlatformSidebar';

/*
 * The signed-in app shell.
 *
 * What the sidebar contains is no longer decided here — `navigation/platformNav`
 * owns the model and `navigation/PlatformSidebar` draws it, because the
 * marketing homepage mounts the same rail beside the real Dashboard. What is
 * still decided here is everything that only a signed-in shell has: the session
 * footer, the mobile drawer's open state, the verification banner, the workflow
 * bell and the chat widget.
 */

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleAdmin = () => setAdminOpen((value) => !value);

  const sessionFooter = (
    <div className="pt-6 mt-6 border-t border-gray-800">
      <div className="px-4 pb-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Appearance
        </div>
        <ThemeSegmentedControl />
      </div>
      <div className="flex items-center px-4 py-3 text-sm">
        <UserCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
        <span className="text-gray-300">{user?.email || 'User'}</span>
      </div>
      <button
        onClick={handleLogout}
        className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-lg hover:bg-gray-800 hover:text-white transition-colors"
      >
        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
        Logout
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 z-50 flex">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />

          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="relative flex w-full max-w-xs flex-col bg-gray-900 border-r border-gray-800"
          >
            <div className="absolute top-0 right-0 p-4">
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <PlatformSidebar
              user={user}
              hostname={hostname}
              activePath={location.pathname}
              variant="mobile"
              adminOpen={adminOpen}
              onToggleAdmin={toggleAdmin}
              onNavigate={() => setSidebarOpen(false)}
            />
          </motion.div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-gray-900 border-r border-gray-800">
          <PlatformSidebar
            user={user}
            hostname={hostname}
            activePath={location.pathname}
            variant="desktop"
            adminOpen={adminOpen}
            onToggleAdmin={toggleAdmin}
            footer={sessionFooter}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-10 lg:hidden bg-gray-900/80 backdrop-blur-xl border-b border-gray-800">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="text-xl font-bold text-gradient">🐻 Bluebear.ai</div>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-gray-400 hover:text-gray-300"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        <main className="min-h-screen">
          <EmailVerificationBanner />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>

      {/* Global workflow-completion notifications */}
      <WorkflowNotificationBell />

      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
}
